import React from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket, Globe, Share2, Search, Mail, Bot, Palette, BarChart3, Target,
  Sparkles, Gem, ShieldCheck, MapPin, Users, TrendingUp, Phone, ArrowUpRight, Check,
} from 'lucide-react';
import '@/styles/boost.css';
import '@/styles/location.css';
import Starfield from '@/components/Starfield';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useReveal from '@/hooks/useReveal';
import { useSeo } from '@/lib/seo';
import {
  BOOST_PHONE_DISPLAY, BOOST_PHONE_E164, BOOST_EMAIL, BOOST_OG,
} from '@/data/locationsData';

const ICONS = {
  Rocket, Globe, Share2, Search, Mail, Bot, Palette, BarChart3, Target,
  Sparkles, Gem, ShieldCheck, MapPin, Users, TrendingUp,
};

function Reveal({ children, className = '', delay = 0, as: Tag = 'div', ...rest }) {
  const ref = useReveal();
  return (
    <Tag ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}

function buildJsonLd(d) {
  const url = `https://boostmarketing.agency/${d.slug}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      '@id': `${url}#business`,
      name: 'BOOST! Design & Marketing',
      image: BOOST_OG,
      url,
      telephone: BOOST_PHONE_E164,
      email: BOOST_EMAIL,
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: d.nap.street,
        addressLocality: d.nap.locality,
        addressRegion: d.nap.region,
        postalCode: d.nap.postcode,
        addressCountry: d.nap.country,
      },
      geo: { '@type': 'GeoCoordinates', latitude: d.nap.lat, longitude: d.nap.lng },
      areaServed: { '@type': 'City', name: d.city },
      description: d.metaDescription,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: d.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://boostmarketing.agency/' },
        { '@type': 'ListItem', position: 2, name: `Marketing ${d.city}`, item: url },
      ],
    },
  ];
}

export default function LocationPage({ data: d }) {
  const url = `https://boostmarketing.agency/${d.slug}`;
  useSeo({
    title: d.title,
    description: d.metaDescription,
    canonical: url,
    image: BOOST_OG,
    jsonLd: buildJsonLd(d),
  });

  return (
    <div className="boost-app" data-testid={`location-page-${d.slug}`}>
      <Starfield />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="section loc-hero" aria-label={`Marketing agency in ${d.city}`}>
          <div className="section-inner loc-hero-inner">
            <Reveal as="span" className="section-eyebrow">{d.eyebrow}</Reveal>
            <Reveal as="h1" className="loc-h1" delay={60}>{d.h1}</Reveal>
            <Reveal className="loc-hero-sub" delay={120}>{d.heroIntro}</Reveal>
            <Reveal className="loc-hero-points" delay={180}>
              {d.heroPoints.map((p) => (
                <span className="loc-point" key={p}><Check size={15} /> {p}</span>
              ))}
            </Reveal>
            <Reveal className="loc-hero-ctas" delay={240}>
              <Link to="/#contact" className="boost-btn boost-btn-primary boost-btn-lg" data-testid="loc-cta-contact">
                <Rocket size={18} /> Start a Project
              </Link>
              <a href={`tel:${BOOST_PHONE_E164}`} className="boost-btn boost-btn-ghost boost-btn-lg" data-testid="loc-cta-call">
                <Phone size={16} /> {BOOST_PHONE_DISPLAY}
              </a>
            </Reveal>
          </div>
        </section>

        {/* Intro */}
        <section className="section loc-intro">
          <div className="section-inner loc-narrow">
            <Reveal as="h2" className="section-title">{d.introHeading}</Reveal>
            {d.introCopy.map((p, i) => (
              <Reveal as="p" className="loc-lead" delay={i * 80} key={p.slice(0, 20)}>{p}</Reveal>
            ))}
          </div>
        </section>

        {/* Services */}
        <section className="section loc-services" id="services">
          <div className="section-inner">
            <Reveal className="section-head">
              <span className="section-eyebrow">// SERVICES</span>
              <h2 className="section-title">{d.servicesHeading}</h2>
            </Reveal>
            <div className="loc-grid">
              {d.services.map((s, i) => {
                const Icon = ICONS[s.icon] || Sparkles;
                return (
                  <Reveal className="ai-card loc-card" delay={(i % 3) * 70} key={s.title}>
                    <div className="ai-card-icon"><Icon size={24} /></div>
                    <h3 className="ai-card-title">{s.title}</h3>
                    <p className="ai-card-desc">{s.desc}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why choose us */}
        <section className="section loc-why">
          <div className="section-inner">
            <Reveal className="section-head">
              <span className="section-eyebrow">// WHY BOOST!</span>
              <h2 className="section-title">{d.whyHeading}</h2>
            </Reveal>
            <div className="loc-why-grid">
              {d.why.map((w, i) => {
                const Icon = ICONS[w.icon] || Sparkles;
                return (
                  <Reveal className="loc-why-card" delay={(i % 2) * 70} key={w.title}>
                    <span className="loc-why-icon"><Icon size={20} /></span>
                    <div>
                      <h3 className="loc-why-title">{w.title}</h3>
                      <p className="ai-card-desc">{w.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Local client / proof */}
        <section className="section loc-proof">
          <div className="section-inner loc-narrow">
            <Reveal className="loc-proof-card">
              <span className="section-eyebrow">// {d.localClient.heading.toUpperCase()}</span>
              <p className="loc-lead">{d.localClient.copy}</p>
              <div className="loc-clients">
                <span className="loc-client-chip" data-testid="loc-local-client">
                  <span className="loc-client-name">{d.localClient.name}</span>
                  <span className="loc-client-tag">{d.localClient.tag}</span>
                </span>
                <Link to="/case-studies/diamondbrite" className="loc-client-chip loc-client-link">
                  <span className="loc-client-name">Diamondbrite</span>
                  <span className="loc-client-tag">Read case study <ArrowUpRight size={12} /></span>
                </Link>
                <Link to="/case-studies/temple-of-sound" className="loc-client-chip loc-client-link">
                  <span className="loc-client-name">Temple of Sound</span>
                  <span className="loc-client-tag">Read case study <ArrowUpRight size={12} /></span>
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Areas served + map */}
        <section className="section loc-areas">
          <div className="section-inner">
            <Reveal className="section-head">
              <span className="section-eyebrow">// COVERAGE</span>
              <h2 className="section-title">{d.areasHeading}</h2>
            </Reveal>
            <div className="loc-areas-grid">
              <Reveal className="loc-chips">
                {d.areas.map((a) => <span className="loc-area-chip" key={a}><MapPin size={13} /> {a}</span>)}
              </Reveal>
              <Reveal className="loc-map" delay={120}>
                <iframe
                  title={`Map of ${d.city}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(d.mapQuery)}&output=embed`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Reveal>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section loc-faq">
          <div className="section-inner loc-narrow">
            <Reveal className="section-head">
              <span className="section-eyebrow">// FAQ</span>
              <h2 className="section-title">Marketing in {d.city} — your questions</h2>
            </Reveal>
            <div className="loc-faq-list">
              {d.faqs.map((f, i) => (
                <Reveal className="loc-faq-item" delay={i * 50} key={f.q}>
                  <h3 className="loc-faq-q">{f.q}</h3>
                  <p className="ai-card-desc">{f.a}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA + NAP */}
        <section className="section loc-cta">
          <div className="section-inner loc-cta-inner">
            <Reveal>
              <h2 className="section-title">Let’s grow your {d.city} business</h2>
              <p className="loc-lead">Tell us your goals and we’ll plot the flight path — we reply within 24 hours.</p>
              <div className="loc-hero-ctas">
                <Link to="/#contact" className="boost-btn boost-btn-primary boost-btn-lg" data-testid="loc-cta-contact-bottom">
                  <Rocket size={18} /> Start a Project
                </Link>
                <a href={`mailto:${BOOST_EMAIL}`} className="boost-btn boost-btn-ghost boost-btn-lg">
                  <Mail size={16} /> {BOOST_EMAIL}
                </a>
              </div>
              <address className="loc-nap">
                <span><strong>BOOST! Design &amp; Marketing</strong></span>
                <span>{d.nap.street}, {d.nap.locality}, {d.nap.region}, {d.nap.postcode}</span>
                <span>
                  <a href={`tel:${BOOST_PHONE_E164}`}>{BOOST_PHONE_DISPLAY}</a> ·{' '}
                  <a href={`mailto:${BOOST_EMAIL}`}>{BOOST_EMAIL}</a>
                </span>
              </address>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
